import { Badge } from '@/components/ui/Badge'

/**
 * DESIGN.md calls the layered product-UI card inside a dark hero the strongest
 * signature pattern of the system. Here it is a tail-rotation Gantt sketch.
 *
 * Static illustration — NOT the real Gantt component.
 */

interface Leg {
  /** Percent from the left of the card's time axis. */
  start: number
  width: number
  label: string
  tone?: 'normal' | 'conflict' | 'ground'
}

const ROWS: { tail: string; legs: Leg[] }[] = [
  {
    tail: 'VN-A321',
    legs: [
      { start: 0, width: 26, label: 'SGN–HAN' },
      { start: 30, width: 24, label: 'HAN–DAD' },
      { start: 58, width: 28, label: 'DAD–SGN' },
    ],
  },
  {
    tail: 'VN-A322',
    legs: [
      { start: 6, width: 22, label: 'HAN–CXR' },
      { start: 32, width: 30, label: 'CXR–SGN' },
      { start: 66, width: 20, label: 'SGN–PQC' },
    ],
  },
  {
    tail: 'VN-A323',
    legs: [
      { start: 0, width: 18, label: 'SGN–VII' },
      { start: 24, width: 34, label: 'AOG', tone: 'ground' },
      { start: 62, width: 26, label: 'VII–SGN', tone: 'conflict' },
    ],
  },
  {
    tail: 'VN-A324',
    legs: [
      { start: 10, width: 28, label: 'HAN–BKK' },
      { start: 44, width: 30, label: 'BKK–HAN' },
    ],
  },
]

const TONE: Record<NonNullable<Leg['tone']>, string> = {
  normal: 'bg-white/12 text-on-dark border-white/10',
  conflict: 'bg-semantic-down/25 text-white border-semantic-down/50',
  ground: 'bg-accent-yellow/20 text-accent-yellow border-accent-yellow/30',
}

export function GanttPreviewCard() {
  return (
    <div className="relative">
      {/* Offset backing card — depth from layering, not from shadow. */}
      <div
        aria-hidden="true"
        className="absolute inset-0 translate-x-4 translate-y-4 rounded-[24px]
                   bg-surface-dark-elevated/60"
      />

      <div className="relative rounded-[24px] bg-surface-dark-elevated p-8">
        <div className="mb-6 flex items-start justify-between gap-4">
          <div>
            <p className="t-title-md text-on-dark">Tail rotation</p>
            <p className="t-caption mt-1 text-on-dark-soft">09 SEP · UTC · 4 tails · 11 legs</p>
          </div>
          <Badge tone="dark">LIVE</Badge>
        </div>

        <div className="mb-3 flex justify-between px-1">
          {['00', '06', '12', '18', '24'].map((h) => (
            <span key={h} className="t-caption-strong font-mono text-on-dark-soft/60">
              {h}
            </span>
          ))}
        </div>

        <div className="space-y-2.5">
          {ROWS.map((row) => (
            <div key={row.tail} className="flex items-center gap-3">
              <span className="t-caption-strong w-16 shrink-0 font-mono text-on-dark-soft">
                {row.tail}
              </span>
              <div className="relative h-7 flex-1 rounded-[8px] bg-white/4">
                {row.legs.map((leg) => (
                  <div
                    key={leg.label}
                    className={`absolute top-0 flex h-7 items-center overflow-hidden
                                rounded-[6px] border px-2 ${TONE[leg.tone ?? 'normal']}`}
                    style={{ left: `${leg.start}%`, width: `${leg.width}%` }}
                  >
                    <span className="t-caption-strong truncate">{leg.label}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-6 flex items-center gap-5 border-t border-white/10 pt-5">
          <Legend className="bg-semantic-down/60" label="1 conflict" />
          <Legend className="bg-accent-yellow/60" label="1 AOG" />
        </div>
      </div>
    </div>
  )
}

function Legend({ className, label }: { className: string; label: string }) {
  return (
    <span className="flex items-center gap-2">
      <span className={`h-2 w-2 rounded-full ${className}`} aria-hidden="true" />
      <span className="t-caption text-on-dark-soft">{label}</span>
    </span>
  )
}
